import type { FieldPath, FieldValues, PathValue } from "react-hook-form"
import { useController } from "react-hook-form"

import type { Option } from "@/nzds"

import { Autocomplete } from "@mui/material"

import { TextField } from "../../inputs/text-field/text-field"
import type { TextFieldProps } from "../../inputs/text-field/text-field.types"
import type { FormAutocompleteProps } from "./form-autocomplete.types"

interface OptionWithId {
  id: number | string
}

type TextFieldSlotProps = NonNullable<TextFieldProps["slotProps"]>
type TextFieldInputSlotProps = TextFieldSlotProps["input"]

const isOptionWithId = (value: unknown): value is OptionWithId =>
  typeof value === "object" &&
  value !== null &&
  "id" in value &&
  (typeof (value as OptionWithId).id === "number" ||
    typeof (value as OptionWithId).id === "string")

const getFieldValueFromSelection = (selectedValue: unknown): unknown => {
  if (selectedValue === null) {
    return null
  }

  if (Array.isArray(selectedValue)) {
    return selectedValue.map((item: unknown) =>
      isOptionWithId(item) ? item.id : item
    )
  }

  if (isOptionWithId(selectedValue)) {
    return selectedValue.id
  }

  return selectedValue
}

const mergeHtmlInputSlotProps = (
  autocompleteSlotProps: TextFieldSlotProps | undefined,
  consumerSlotProps: TextFieldSlotProps | undefined
) => ({
  ...(typeof autocompleteSlotProps?.htmlInput === "object"
    ? autocompleteSlotProps.htmlInput
    : undefined),
  ...(typeof consumerSlotProps?.htmlInput === "object"
    ? consumerSlotProps.htmlInput
    : undefined),
})

const mergeInputSlotProps = (
  autocompleteInputSlot: TextFieldInputSlotProps | undefined,
  consumerInputSlot: TextFieldInputSlotProps | undefined
): TextFieldInputSlotProps | undefined => {
  if (
    typeof autocompleteInputSlot !== "function" &&
    typeof consumerInputSlot !== "function"
  ) {
    if (!autocompleteInputSlot && !consumerInputSlot) {
      return undefined
    }

    return {
      ...(typeof autocompleteInputSlot === "object"
        ? autocompleteInputSlot
        : undefined),
      ...(typeof consumerInputSlot === "object"
        ? consumerInputSlot
        : undefined),
    }
  }

  return ((ownerState) => {
    const autocompleteProps =
      typeof autocompleteInputSlot === "function"
        ? autocompleteInputSlot(ownerState)
        : (autocompleteInputSlot ?? {})

    const consumerProps =
      typeof consumerInputSlot === "function"
        ? consumerInputSlot(ownerState)
        : (consumerInputSlot ?? {})

    return {
      ...autocompleteProps,
      ...consumerProps,
    }
  }) as TextFieldInputSlotProps
}

const buildTextFieldSlotProps = (
  autocompleteSlotProps: TextFieldSlotProps | undefined,
  consumerSlotProps: TextFieldSlotProps | undefined
): TextFieldSlotProps | undefined => {
  if (!consumerSlotProps) {
    return autocompleteSlotProps
  }

  const mergedInput = mergeInputSlotProps(
    autocompleteSlotProps?.input,
    consumerSlotProps.input
  )

  return {
    ...autocompleteSlotProps,
    ...consumerSlotProps,
    htmlInput: mergeHtmlInputSlotProps(
      autocompleteSlotProps,
      consumerSlotProps
    ),
    ...(mergedInput === undefined ? {} : { input: mergedInput }),
  }
}

export const FormAutocomplete = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends Option = Option,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>({
  disabled = false,
  error,
  fullWidth = false,
  helperText,
  id,
  label,
  name,
  onChange: onChangeProp,
  onKeyDown,
  placeholder,
  preserveHelperTextSpacing = false,
  size,
  slotProps: consumerSlotProps,
  value: valueProp,
  variant,
  ...rest
}: FormAutocompleteProps<
  TFieldValues,
  TName,
  TOption,
  Multiple,
  DisableClearable,
  FreeSolo
>) => {
  const {
    field: {
      ref: fieldRef,
      value: fieldValue,
      onChange: fieldOnChange,
      ...restField
    },
    fieldState,
  } = useController<TFieldValues, TName>({
    disabled,
    name,
  })

  const helperTextValue = helperText ?? (preserveHelperTextSpacing ? " " : "")

  return (
    <Autocomplete
      {...rest}
      {...restField}
      disabled={disabled}
      fullWidth={fullWidth}
      id={id ?? name}
      onChange={(event, selectedValue, reason, details) => {
        if (onChangeProp) {
          onChangeProp(event, selectedValue, reason, details)

          return
        }

        fieldOnChange(
          getFieldValueFromSelection(selectedValue) as PathValue<
            TFieldValues,
            TName
          >
        )
      }}
      value={valueProp ?? fieldValue}
      renderInput={(params) => {
        const textFieldSlotProps = buildTextFieldSlotProps(
          params.slotProps as TextFieldSlotProps | undefined,
          consumerSlotProps
        )

        return (
          <TextField
            {...params}
            error={Boolean(fieldState.error) || error}
            fullWidth={fullWidth}
            helperText={
              fieldState.error ? fieldState.error.message : helperTextValue
            }
            inputRef={fieldRef}
            label={label ?? ""}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            slotProps={textFieldSlotProps}
            variant={variant}
          />
        )
      }}
    />
  )
}
